// Employee Profile screen — People Hub > My Team > {Employee Name}
// Composes 5 modular sections: Employee Details, Last Twelve Weeks (reused), Coaching History,
// All History, and (new hires only) Onboarding Checklist.
import React, { useEffect, useMemo } from "react";
import { View, StyleSheet, ScrollView, ActivityIndicator } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { AdminColors } from "../../Admin/constants/adminColors";
import { MyTeamMember } from "../types";
import type { EmployeeAllHistoryRow } from "./types";
import LastTwelveWeeks from "../BeginActivity/common/components/LastTwelveWeeks";
import EmployeeDetailsCard from "./components/EmployeeDetailsCard";
import EmployeeCoachingHistoryCard from "./components/EmployeeCoachingHistoryCard";
import EmployeeAllHistoryCard from "./components/EmployeeAllHistoryCard";
import OnboardingChecklistCard from "./components/OnboardingChecklistCard";
import {
  buildEmployeeProfileDetails,
  buildEmployeeMetricRows,
  buildEmployeeOnboardingPlan,
} from "./mockEmployeeProfileData";
import {
  EmployeeProfileApiResponse,
  mapEmployeeToMember,
  mapEmployeeProfileDetails,
  mapWeeklyMetricRows,
  mapPeriodMetricRows,
  mapCoachingItems,
  mapRecentActivities,
  mapEmployeeOnboardingPlan,
} from "./mapEmployeeProfileResponse";
import { fetchEmployeeProfileData, resetEmployeeProfile } from "../../../store/slices/peopleHubSlice";
import type { AppDispatch, RootState } from "../../../store";

interface EmployeeProfileScreenProps {
  member: MyTeamMember;
  onEdit?: (member: MyTeamMember) => void;
  onBeginActivity?: (member: MyTeamMember) => void;
  /** Fired by both Resume (draft) and View (finished) in All History — parent re-opens the activity. */
  onResumeActivity?: (row: EmployeeAllHistoryRow) => void;
  /** Opens the Employee Profile screen for the clicked Employee Name cell in All History. */
  onOpenEmployee?: (employeeName: string, employeeId?: string, employeeRole?: string) => void;
  /** Bump this after a successful edit to force a re-fetch of the profile data. */
  refreshToken?: number;
}

const EmployeeProfileScreen: React.FC<EmployeeProfileScreenProps> = ({
  member,
  onEdit,
  onBeginActivity,
  onResumeActivity,
  onOpenEmployee,
  refreshToken,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((state: RootState) => state.peopleHub.employeeProfile.data) as EmployeeProfileApiResponse | null;
  const isLoading = useSelector((state: RootState) => state.peopleHub.employeeProfile.loadingState === "loading");

  const refreshProfile = () => {
    dispatch(fetchEmployeeProfileData({ employeeId: member.gpid, clientTimestamp: new Date().toISOString() }));
  };

  useEffect(() => {
    refreshProfile();
    // Clear the loaded profile once the user navigates away from this screen.
    return () => {
      dispatch(resetEmployeeProfile());
    };
  }, [dispatch, member.gpid, refreshToken]);

  const resolvedMember = useMemo(
    () => (profile?.employee ? mapEmployeeToMember(member, profile.employee) : member),
    [member, profile],
  );

  const details = useMemo(
    () => mapEmployeeProfileDetails(profile ?? { employee: {} }, buildEmployeeProfileDetails(resolvedMember)),
    [profile, resolvedMember],
  );

  const metricRows = useMemo(() => {
    const mapped = mapWeeklyMetricRows(profile?.metrics12Week);
    return mapped.length > 0 ? mapped : buildEmployeeMetricRows();
  }, [profile]);

  const periodMetricRows = useMemo(
    () => mapPeriodMetricRows(profile?.metrics12Period),
    [profile],
  );

  const coachingRows = useMemo(
    () => mapCoachingItems(profile?.coachingItems, resolvedMember.name),
    [profile, resolvedMember.name],
  );

  const historyRows = useMemo(
    () => mapRecentActivities(profile?.recentActivities, resolvedMember.name, resolvedMember.role),
    [profile, resolvedMember],
  );

  const onboardingPlan = useMemo(
    () => mapEmployeeOnboardingPlan(profile, buildEmployeeOnboardingPlan(resolvedMember)),
    [profile, resolvedMember],
  );

  if (isLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        <ActivityIndicator size="large" color={AdminColors.blue700} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <EmployeeDetailsCard
        member={resolvedMember}
        details={details}
        onEdit={() => onEdit?.(resolvedMember)}
        onBeginActivity={() => onBeginActivity?.(resolvedMember)}
      />

      <View style={styles.card}>
        <LastTwelveWeeks
          title="Last Twelve Weeks"
          rows={metricRows}
          periodRows={periodMetricRows}
          periodTitle="Last Twelve Periods"
          activityType="routine_observation"
        />
      </View>

      <EmployeeCoachingHistoryCard rows={coachingRows} />

      <EmployeeAllHistoryCard
        rows={historyRows}
        onResume={(row) => onResumeActivity?.(row)}
        onView={(row) => onResumeActivity?.(row)}
        onOpenEmployee={onOpenEmployee}
      />

      <OnboardingChecklistCard
        plan={onboardingPlan}
        member={resolvedMember}
        onRefreshProfile={refreshProfile}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  loadingContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: 20,
    gap: 16,
  },
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    padding: 20,
  },
});

export default EmployeeProfileScreen;
